import React from 'react';
import { Loader2, X } from 'lucide-react';
import { apiClient } from '../../../lib/apiClient';
import Banner from './Banner';

export interface ArchivedFileCollisionModalProps {
  slug: string;
  advertiserId: string;
  // Picked file the user just chose from disk.
  filename: string;
  sizeBytes: number;
  // The archived tile's existing storage_path — handed to the slot when the
  // user picks "Re-use archived" so the wizard skips uploading.
  archivedStoragePath: string;
  // Optional mime type from the archived row; defaults to "video/mp4".
  archivedMimeType?: string | null;
  accentColor?: string;
  onClose: () => void;
  // Either branch starts by DELETE-ing the archive row. Then:
  //   - reuse: caller attaches the archived storage path to the slot.
  //   - upload: caller proceeds with the normal upload pipeline.
  onReuseArchived: (info: {
    storagePath: string;
    filename: string;
    sizeBytes: number;
    mimeType: string;
  }) => void;
  onUploadAnyway: () => void;
}

// CTV-526: surfaced before an upload starts when the picked file's
// (filename, sizeBytes) matches an archived tile at the wizard's
// (station, advertiser). Both branches unarchive — see CONTEXT.md
// "Upload-collision prompt" for the invariant.
const ArchivedFileCollisionModal: React.FC<ArchivedFileCollisionModalProps> = ({
  slug,
  advertiserId,
  filename,
  sizeBytes,
  archivedStoragePath,
  archivedMimeType,
  accentColor,
  onClose,
  onReuseArchived,
  onUploadAnyway,
}) => {
  const [busy, setBusy] = React.useState<null | 'reuse' | 'upload'>(null);
  const [error, setError] = React.useState<string | null>(null);

  const unarchive = async () => {
    await apiClient.delete(`/creatives/library/${slug}/archive`, {
      advertiserId,
      filename,
      sizeBytes,
    });
  };

  const handleReuse = async () => {
    setBusy('reuse');
    setError(null);
    try {
      await unarchive();
      onReuseArchived({
        storagePath: archivedStoragePath,
        filename,
        sizeBytes,
        mimeType: archivedMimeType || 'video/mp4',
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong.');
      setBusy(null);
    }
  };

  const handleUpload = async () => {
    setBusy('upload');
    setError(null);
    try {
      await unarchive();
      onUploadAnyway();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong.');
      setBusy(null);
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-slate-900/45 backdrop-blur-[2px] z-50"
        onClick={(e) => {
          e.stopPropagation();
          if (busy === null) onClose();
        }}
      />
      <div
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90%] max-w-[460px] bg-white rounded-2xl border border-slate-200/80 overflow-hidden"
        style={{
          boxShadow:
            '0 24px 48px -16px rgba(15,23,42,0.28), 0 2px 4px -1px rgba(15,23,42,0.05)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="h-[3px]"
          style={{
            background: `linear-gradient(90deg, transparent, ${
              accentColor || '#475569'
            }, transparent)`,
          }}
        />
        <div className="p-5">
          <div className="flex items-start justify-between mb-2.5">
            <div className="min-w-0">
              <h3 className="text-[15px] font-semibold tracking-tight text-slate-900 leading-tight">
                You've used this file before
              </h3>
              <p className="text-[12px] text-slate-500 mt-1">
                It's currently archived in your library. Pick what to do.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={busy !== null}
              aria-label="Close"
              className="p-1 -mr-1 -mt-1 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors disabled:opacity-50"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <Banner tone="info" accentColor={accentColor} label="Matched file">
            <div className="font-medium text-slate-800 truncate">{filename}</div>
            <div className="text-[11.5px] text-slate-500 mt-0.5">
              {(sizeBytes / (1024 * 1024)).toFixed(1)} MB
            </div>
          </Banner>

          {error && (
            <p className="text-[12px] text-red-600 mt-3 leading-relaxed">
              {error}
            </p>
          )}

          <div className="flex flex-col gap-2 mt-5">
            <button
              type="button"
              onClick={handleReuse}
              disabled={busy !== null}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-[12.5px] font-semibold text-white transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
              style={{
                background: `linear-gradient(180deg, ${
                  accentColor || '#475569'
                }, ${accentColor || '#1e293b'})`,
                boxShadow:
                  '0 1px 0 rgba(255,255,255,0.25) inset, 0 6px 14px -8px rgba(15,23,42,0.5)',
              }}
            >
              {busy === 'reuse' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              Re-use the archived file
            </button>
            <button
              type="button"
              onClick={handleUpload}
              disabled={busy !== null}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-[12.5px] font-medium text-slate-700 bg-white border border-slate-300 hover:border-slate-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busy === 'upload' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              Upload anyway (new copy)
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ArchivedFileCollisionModal;
